import type { CampaignPerformance, ChannelPerformanceRow } from '@/api/campaigns'
import type { Envelope } from '@/api/types'
import {
  Card,
  DataTable,
  EmptyState,
  formatCurrency,
  formatInteger,
  formatPercent,
  formatRatio,
  KpiCard,
  LoadingBlock,
  type Column,
  type FormatOptions,
} from '@/design'
import { drillDownHref } from '@/lib/drilldown'
import { useGlobalFilters } from '@/lib/useGlobalFilters'

import { KindBadge } from '../components'

function none(label = '—') {
  return <span className="camp-muted">{label}</span>
}

function columns(money: FormatOptions): Column<ChannelPerformanceRow>[] {
  return [
    {
      key: 'channel',
      header: 'Channel',
      sortValue: (row) => row.name,
      cell: (row) => (
        <span className="camp-inline">
          {row.name}
          <KindBadge kind={row.kind} />
        </span>
      ),
    },
    { key: 'visits', header: 'Visits & clicks', align: 'end', sortValue: (row) => row.visits + row.clicks, cell: (row) => formatInteger(row.visits + row.clicks) },
    { key: 'scans', header: 'QR scans', align: 'end', sortValue: (row) => row.scans, cell: (row) => formatInteger(row.scans) },
    { key: 'promo', header: 'Promo uses', align: 'end', sortValue: (row) => row.promoCodeUses, cell: (row) => formatInteger(row.promoCodeUses) },
    { key: 'entries', header: 'Entries', align: 'end', sortValue: (row) => row.entries, cell: (row) => formatInteger(row.entries) },
    { key: 'orders', header: 'Orders', align: 'end', sortValue: (row) => row.orders, cell: (row) => formatInteger(row.orders) },
    { key: 'revenue', header: 'Revenue', align: 'end', sortValue: (row) => row.revenue, cell: (row) => formatCurrency(row.revenue, money) },
    { key: 'spend', header: 'Spend', align: 'end', sortValue: (row) => row.spend ?? -1, cell: (row) => (row.spend === null ? none() : formatCurrency(row.spend, money)) },
    {
      key: 'cpa',
      header: 'CPA',
      align: 'end',
      sortValue: (row) => row.costPerAcquisition ?? Number.POSITIVE_INFINITY,
      cell: (row) => (row.costPerAcquisition === null ? none() : formatCurrency(row.costPerAcquisition, money)),
    },
    {
      key: 'roas',
      header: 'ROAS',
      align: 'end',
      sortValue: (row) => row.returnOnAdSpend ?? -1,
      cell: (row) => (row.returnOnAdSpend === null ? none('No spend') : formatRatio(row.returnOnAdSpend)),
    },
  ]
}

/** Headline figures for the campaign, then every channel — digital and offline — compared on the same terms. */
export function PerformanceSection({
  query,
  money,
}: {
  query: { data?: Envelope<CampaignPerformance>; isPending: boolean; isError: boolean; isFetching: boolean }
  money: FormatOptions
}) {
  const { filters } = useGlobalFilters()
  const performance = query.data?.data
  const period = query.data?.meta.period?.label ?? 'Selected period'

  if (query.isPending) return <LoadingBlock rows={6} label="Loading performance" />
  if (query.isError || !performance) {
    return (
      <Card title="Performance">
        <EmptyState variant="outage" compact description="Performance could not be loaded. The rest of the campaign is unaffected." />
      </Card>
    )
  }

  const { totals, channels } = performance
  const kind = (value: 'digital' | 'offline') => channels.filter((row) => row.kind === value)
  const sum = (rows: readonly ChannelPerformanceRow[], pick: (row: ChannelPerformanceRow) => number) => rows.reduce((total, row) => total + pick(row), 0)
  const digital = kind('digital')
  const offline = kind('offline')

  return (
    <>
      <section className="camp-kpis" aria-label="Campaign performance">
        <KpiCard label="Revenue" value={formatCurrency(totals.revenue, money)} change={null} polarity="up_good" note={period} />
        <KpiCard label="Entries" value={formatInteger(totals.entries)} change={null} polarity="up_good" note={period} />
        <KpiCard label="Orders" value={formatInteger(totals.orders)} change={null} polarity="up_good" note={period} />
        <KpiCard label="Spend" value={totals.spend === null ? '—' : formatCurrency(totals.spend, money)} change={null} polarity="neutral" note={period} />
        <KpiCard
          label="ROAS"
          value={totals.returnOnAdSpend === null ? '—' : formatRatio(totals.returnOnAdSpend)}
          change={null}
          polarity="up_good"
          note={period}
        />
        <KpiCard
          label="Conversion"
          value={totals.conversionRate === null ? '—' : formatPercent(totals.conversionRate)}
          change={null}
          polarity="up_good"
          note="Orders per visit, scan or click"
        />
      </section>

      <Card
        title="Channel performance"
        subtitle={`Online and offline channels on the same terms · ${period}${performance.nonProduction ? ' · includes non-production data' : ''}`}
        busy={query.isFetching}
      >
        {channels.length > 0 ? (
          <dl className="camp-mix" aria-label="Online and offline">
            <div>
              <dt>Digital revenue</dt>
              <dd>{formatCurrency(sum(digital, (row) => row.revenue), money)}</dd>
            </div>
            <div>
              <dt>Digital entries</dt>
              <dd>{formatInteger(sum(digital, (row) => row.entries))}</dd>
            </div>
            <div>
              <dt>Offline revenue</dt>
              <dd>{formatCurrency(sum(offline, (row) => row.revenue), money)}</dd>
            </div>
            <div>
              <dt>Offline entries</dt>
              <dd>{formatInteger(sum(offline, (row) => row.entries))}</dd>
            </div>
            <div>
              <dt>Offline scans</dt>
              <dd>{formatInteger(sum(offline, (row) => row.scans))}</dd>
            </div>
          </dl>
        ) : null}
        <DataTable
          caption="Channel performance"
          columns={columns(money)}
          rows={channels}
          rowKey={(row) => row.channelId}
          rowHref={(row) => drillDownHref(row.drillDown, filters)}
          initialSort={{ key: 'revenue', direction: 'desc' }}
          empty={<EmptyState variant="no-data" compact title="No channels yet" description="Add a channel to start tracking it." />}
        />
        {channels.length > 0 && totals.orders === 0 ? (
          <p className="camp-muted">No tracked activity in this period yet. Figures appear as links are opened, codes scanned and orders attributed.</p>
        ) : null}
      </Card>
    </>
  )
}
